import React, { useState, useEffect } from 'react';
import api, { trustLedgerAPI } from '../lib/api';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Shield,
  DollarSign,
  Plus,
  Search,
  Download,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  User,
  CheckCircle2,
  Clock,
  History,
  Scale,
  Briefcase,
  Eye,
  Loader2,
  Calendar,
  Edit2,
  Trash2,
  Check,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

export const TrustAccounting = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDepositDialogOpen, setIsDepositDialogOpen] = useState(false);
  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false);
  
  // Ledger dialog state
  const [isLedgerDialogOpen, setIsLedgerDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [ledgerData, setLedgerData] = useState({ ledger: [], current_balance: 0 });
  const [ledgerLoading, setLedgerLoading] = useState(false);
  
  const [trustData, setTrustData] = useState({
    summary: {
      totalBalance: 0,
      clientCount: 0,
      pendingDisbursements: 0,
      lastReconciled: null
    },
    clientBalances: [],
    recentTransactions: [],
    complianceAlerts: []
  });

  const [transactionForm, setTransactionForm] = useState({
    client_id: '',
    amount: '',
    description: '',
    reference: '',
    case_id: ''
  });

  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    fetchTrustData();
  }, []);

  const fetchTrustData = async () => {
    try {
      const [trustRes, clientsRes, casesRes] = await Promise.all([
        api.get('/trust/dashboard').catch(() => ({ data: null })),
        api.get('/clients').catch(() => ({ data: [] })),
        api.get('/cases').catch(() => ({ data: [] }))
      ]);
      
      setClients(clientsRes.data || []);
      setCases(casesRes.data || []);
      
      if (trustRes.data) {
        setTrustData(trustRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch trust data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/trust/deposit', transactionForm);
      toast.success('Deposit recorded successfully');
      setIsDepositDialogOpen(false);
      resetForm();
      fetchTrustData();
    } catch (error) {
      console.error('Failed to record deposit:', error);
      toast.error('Failed to record deposit');
    }
  };

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    try {
      await api.post('/trust/withdraw', transactionForm);
      toast.success('Withdrawal recorded successfully');
      setIsWithdrawDialogOpen(false);
      resetForm();
      fetchTrustData();
    } catch (error) {
      console.error('Failed to record withdrawal:', error);
      toast.error('Failed to record withdrawal');
    }
  };

  const resetForm = () => {
    setTransactionForm({
      client_id: '',
      amount: '',
      description: '',
      reference: '',
      case_id: ''
    });
  };

  // View client trust ledger
  const openClientLedger = async (balance) => {
    setSelectedClient(balance);
    setIsLedgerDialogOpen(true);
    setLedgerLoading(true);
    
    try {
      const res = await trustLedgerAPI.getLedger(balance.client_id);
      setLedgerData(res.data);
    } catch (error) {
      console.error('Failed to load ledger:', error);
      toast.error('Failed to load client ledger');
      setLedgerData({
        client_id: balance.client_id,
        client_name: balance.client_name,
        current_balance: balance.balance,
        ledger: []
      });
    } finally {
      setLedgerLoading(false);
    }
  };

  const closeLedgerDialog = () => {
    setIsLedgerDialogOpen(false);
    setSelectedClient(null);
    setLedgerData({ ledger: [], current_balance: 0 });
  };

  const filteredBalances = trustData.clientBalances.filter(b =>
    b.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.case_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <PageLoader text="Loading trust accounting..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="trust-accounting-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Trust Accounting</h1>
          <p className="text-slate-500 mt-1">IOLTA compliant trust account management</p>
        </div>
        <div className="flex items-center gap-3">
          <Dialog open={isDepositDialogOpen} onOpenChange={setIsDepositDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-green-600 hover:bg-green-700" data-testid="new-deposit-btn">
                <ArrowDownRight className="h-4 w-4" />
                Deposit
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Record Trust Deposit</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleDeposit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="deposit-client">Client *</Label>
                  <Select
                    value={transactionForm.client_id}
                    onValueChange={(value) => setTransactionForm(prev => ({ ...prev, client_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.client_id} value={client.client_id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deposit-case">Related Case</Label>
                  <Select
                    value={transactionForm.case_id || "none"}
                    onValueChange={(value) => setTransactionForm(prev => ({ ...prev, case_id: value === "none" ? "" : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select case (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {cases.map((c) => (
                        <SelectItem key={c.case_id} value={c.case_id}>
                          {c.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deposit-amount">Amount *</Label>
                  <Input
                    id="deposit-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={transactionForm.amount}
                    onChange={(e) => setTransactionForm(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deposit-description">Description *</Label>
                  <Textarea
                    id="deposit-description"
                    value={transactionForm.description}
                    onChange={(e) => setTransactionForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="e.g., Initial retainer deposit"
                    rows={2}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deposit-reference">Reference Number</Label>
                  <Input
                    id="deposit-reference"
                    value={transactionForm.reference}
                    onChange={(e) => setTransactionForm(prev => ({ ...prev, reference: e.target.value }))}
                    placeholder="e.g., Check #1234"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsDepositDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700">
                    Record Deposit
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isWithdrawDialogOpen} onOpenChange={setIsWithdrawDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="new-withdrawal-btn">
                <ArrowUpRight className="h-4 w-4" />
                Withdraw
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Record Trust Withdrawal</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleWithdrawal} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="withdraw-client">Client *</Label>
                  <Select
                    value={transactionForm.client_id}
                    onValueChange={(value) => setTransactionForm(prev => ({ ...prev, client_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.client_id} value={client.client_id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="withdraw-case">Related Case</Label>
                  <Select
                    value={transactionForm.case_id || "none"}
                    onValueChange={(value) => setTransactionForm(prev => ({ ...prev, case_id: value === "none" ? "" : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select case (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {cases.map((c) => (
                        <SelectItem key={c.case_id} value={c.case_id}>
                          {c.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="withdraw-amount">Amount *</Label>
                  <Input
                    id="withdraw-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={transactionForm.amount}
                    onChange={(e) => setTransactionForm(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="withdraw-description">Description *</Label>
                  <Textarea
                    id="withdraw-description"
                    value={transactionForm.description}
                    onChange={(e) => setTransactionForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="e.g., Court filing fee payment"
                    rows={2}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="withdraw-reference">Reference Number</Label>
                  <Input
                    id="withdraw-reference"
                    value={transactionForm.reference}
                    onChange={(e) => setTransactionForm(prev => ({ ...prev, reference: e.target.value }))}
                    placeholder="e.g., Check #5678"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsWithdrawDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="destructive">
                    Record Withdrawal
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Compliance Alerts */}
      {trustData.complianceAlerts.length > 0 && (
        <div className="space-y-2">
          {trustData.complianceAlerts.map((alert) => (
            <div 
              key={alert.id}
              className={cn(
                "p-4 rounded-lg flex items-center gap-3",
                alert.type === 'warning' ? "bg-amber-50 border border-amber-200" : "bg-blue-50 border border-blue-200"
              )}
            >
              <AlertTriangle className={cn(
                "h-5 w-5",
                alert.type === 'warning' ? "text-amber-600" : "text-blue-600"
              )} />
              <div className="flex-1">
                <p className={cn(
                  "font-medium",
                  alert.type === 'warning' ? "text-amber-800" : "text-blue-800"
                )}>
                  {alert.message}
                </p>
              </div>
              <span className="text-sm text-slate-500">{formatDate(alert.date)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="trust-total-balance">
          <CardContent className="p-5">
            <div className="p-2 rounded-lg bg-purple-100 w-fit">
              <Shield className="h-5 w-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-600 mt-3">
              {formatCurrency(trustData.summary.totalBalance)}
            </p>
            <p className="text-sm text-slate-500">Total Trust Balance</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="p-2 rounded-lg bg-blue-100 w-fit">
              <User className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-3">
              {trustData.summary.clientCount}
            </p>
            <p className="text-sm text-slate-500">Clients with Balance</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="p-2 rounded-lg bg-amber-100 w-fit">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-3">
              {formatCurrency(trustData.summary.pendingDisbursements)}
            </p>
            <p className="text-sm text-slate-500">Pending Disbursements</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="p-2 rounded-lg bg-green-100 w-fit">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-lg font-bold text-slate-900 mt-3">
              {trustData.summary.lastReconciled ? formatDate(trustData.summary.lastReconciled) : 'Never'}
            </p>
            <p className="text-sm text-slate-500">Last Reconciled</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <DollarSign className="h-4 w-4" />
            Client Balances
          </TabsTrigger>
          <TabsTrigger value="transactions" className="gap-2">
            <History className="h-4 w-4" />
            Transactions
          </TabsTrigger>
          <TabsTrigger value="reconciliation" className="gap-2">
            <Scale className="h-4 w-4" />
            Reconciliation
          </TabsTrigger>
          <TabsTrigger value="nightly-reconcile" className="gap-2" data-testid="nightly-reconcile-tab">
            <Briefcase className="h-4 w-4" />
            Nightly Reconcile
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Client Trust Balances</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search clients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="client-balances-table">
                  <thead className="bg-slate-50 border-y">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Client</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Related Case</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Balance</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Last Activity</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredBalances.map((balance) => (
                      <tr key={balance.client_id} className="hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                            <span className="font-medium text-slate-900">{balance.client_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {balance.case_name && (
                            <Badge variant="outline" className="gap-1">
                              <Briefcase className="h-3 w-3" />
                              {balance.case_name}
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-semibold text-purple-600">
                            {formatCurrency(balance.balance)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-sm text-slate-500">
                          {formatDate(balance.last_activity)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="gap-1"
                            onClick={() => openClientLedger(balance)}
                            data-testid={`view-ledger-${balance.client_id}`}
                          >
                            <Eye className="h-4 w-4" />
                            Ledger
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Recent Trust Transactions</CardTitle>
                <Button variant="outline" className="gap-2">
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="trust-transactions-table">
                  <thead className="bg-slate-50 border-y">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Client</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Description</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Reference</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {trustData.recentTransactions.map((tx) => (
                      <tr key={tx.id} className="hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm text-slate-500">
                          {formatDate(tx.date)}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={tx.type === 'deposit' ? 'default' : 'secondary'} className="gap-1">
                            {tx.type === 'deposit' ? (
                              <ArrowDownRight className="h-3 w-3" />
                            ) : (
                              <ArrowUpRight className="h-3 w-3" />
                            )}
                            {tx.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 font-medium text-slate-900">
                          {tx.client_name}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {tx.description}
                        </td>
                        <td className="py-3 px-4">
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                            {typeof tx.reference === 'object' ? tx.reference?.reference_no || tx.reference?.method || '-' : tx.reference || '-'}
                          </code>
                        </td>
                        <td className={cn(
                          "py-3 px-4 text-right font-semibold",
                          tx.type === 'deposit' ? "text-green-600" : "text-red-600"
                        )}>
                          {tx.type === 'deposit' ? '+' : '-'}{formatCurrency(tx.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Reconciliation Tab */}
        <TabsContent value="reconciliation" className="mt-6">
          <ReconciliationSection trustBalance={trustData.summary.totalBalance} />
        </TabsContent>

        <TabsContent value="nightly-reconcile" className="mt-6">
          <NightlyReconcileSection />
        </TabsContent>
      </Tabs>

      {/* Client Ledger Dialog */}
      <Dialog open={isLedgerDialogOpen} onOpenChange={(open) => { if (!open) closeLedgerDialog(); }}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Trust Ledger - {selectedClient?.client_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Balance Card */}
            <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700">Current Trust Balance</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {formatCurrency(ledgerData.current_balance || selectedClient?.balance || 0)}
                  </p>
                </div>
                <Shield className="h-10 w-10 text-purple-400" />
              </CardContent>
            </Card>
            
            {/* Ledger Table */}
            <div className="border rounded-lg">
              <div className="p-3 bg-slate-100 border-b flex items-center justify-between">
                <h4 className="font-medium text-sm">
                  Transaction History ({ledgerData.ledger?.length || 0} transactions)
                </h4>
                <Button variant="outline" size="sm" className="gap-1">
                  <Download className="h-3 w-3" />
                  Export
                </Button>
              </div>
              <ScrollArea className="h-[350px]">
                {ledgerLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  </div>
                ) : ledgerData.ledger?.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3 text-xs font-medium text-slate-600">Date</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-slate-600">Type</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-slate-600">Description</th>
                        <th className="text-right py-2 px-3 text-xs font-medium text-slate-600">Amount</th>
                        <th className="text-right py-2 px-3 text-xs font-medium text-slate-600">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y text-sm">
                      {ledgerData.ledger?.map((entry, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="py-2 px-3 text-slate-500">
                            {formatDate(entry.created_at)}
                          </td>
                          <td className="py-2 px-3">
                            <Badge 
                              variant="outline"
                              className={cn(
                                "gap-1",
                                entry.type === 'deposit' || entry.type === 'transfer_in'
                                  ? "border-green-300 text-green-700 bg-green-50" 
                                  : "border-red-300 text-red-700 bg-red-50"
                              )}
                            >
                              {entry.type === 'deposit' || entry.type === 'transfer_in' ? (
                                <ArrowDownRight className="h-3 w-3" />
                              ) : (
                                <ArrowUpRight className="h-3 w-3" />
                              )}
                              {entry.type === 'deposit' ? 'Deposit' : 
                               entry.type === 'withdrawal' ? 'Withdrawal' : 
                               entry.type === 'transfer_in' ? 'Transfer In' : 'Transfer Out'}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-slate-700">
                            {entry.description}
                          </td>
                          <td className={cn(
                            "py-2 px-3 text-right font-medium",
                            entry.type === 'deposit' || entry.type === 'transfer_in'
                              ? "text-green-600" 
                              : "text-red-600"
                          )}>
                            {entry.type === 'deposit' || entry.type === 'transfer_in' ? '+' : '-'}
                            {formatCurrency(entry.amount)}
                          </td>
                          <td className="py-2 px-3 text-right font-semibold text-purple-600">
                            {formatCurrency(entry.running_balance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </ScrollArea>
            </div>
          </div>
          
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={closeLedgerDialog}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Reconciliation Component
const ReconciliationSection = ({ trustBalance }) => {
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconciliations, setReconciliations] = useState([]);
  const [bankBalance, setBankBalance] = useState('');
  const [statementDate, setStatementDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadReconciliations();
  }, []);

  const loadReconciliations = async () => {
    setLoading(true);
    try {
      const res = await trustLedgerAPI.getReconciliations(12);
      setReconciliations(res.data?.reconciliations || []);
    } catch (error) {
      console.error('Failed to load reconciliations:', error);
      setReconciliations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReconcile = async (e) => {
    e.preventDefault();
    if (!bankBalance || !statementDate) {
      toast.error('Please enter bank balance and statement date');
      return;
    }
    
    setReconciling(true);
    try {
      await trustLedgerAPI.reconcile(parseFloat(bankBalance), statementDate, notes || null);
      toast.success('Reconciliation completed');
      setBankBalance('');
      setStatementDate('');
      setNotes('');
      loadReconciliations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };

  const bookBalance = trustBalance || 0;
  const bankBalanceNum = parseFloat(bankBalance) || 0;
  const difference = bankBalanceNum - bookBalance;

  return (
    <div className="space-y-6">
      {/* New Reconciliation Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Scale className="h-5 w-5 text-blue-600" />
            Bank Statement Reconciliation
          </CardTitle>
          <CardDescription>
            Compare your trust account bank statement with book balance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleReconcile} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Book Balance (System)</Label>
                <div className="h-10 px-3 flex items-center bg-slate-100 rounded-md border">
                  <span className="font-semibold text-purple-600">{formatCurrency(bookBalance)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bankBalance">Bank Statement Balance *</Label>
                <Input
                  id="bankBalance"
                  type="number"
                  step="0.01"
                  value={bankBalance}
                  onChange={(e) => setBankBalance(e.target.value)}
                  placeholder="Enter bank balance"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="statementDate">Statement Date *</Label>
                <Input
                  id="statementDate"
                  type="date"
                  value={statementDate}
                  onChange={(e) => setStatementDate(e.target.value)}
                  required
                />
              </div>
            </div>
            
            {bankBalance && (
              <div className={cn(
                "p-4 rounded-lg flex items-center justify-between",
                Math.abs(difference) < 0.01 
                  ? "bg-green-50 border border-green-200"
                  : "bg-red-50 border border-red-200"
              )}>
                <div className="flex items-center gap-3">
                  {Math.abs(difference) < 0.01 ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  )}
                  <div>
                    <p className={cn(
                      "font-medium",
                      Math.abs(difference) < 0.01 ? "text-green-800" : "text-red-800"
                    )}>
                      {Math.abs(difference) < 0.01 ? 'Accounts Balance!' : 'Discrepancy Detected'}
                    </p>
                    <p className="text-sm text-slate-600">
                      Difference: {formatCurrency(Math.abs(difference))} 
                      {difference !== 0 && (
                        <span className="ml-1">
                          ({difference > 0 ? 'Bank shows more' : 'Book shows more'})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any reconciliation notes..."
                rows={2}
              />
            </div>
            
            <div className="flex justify-end">
              <Button type="submit" disabled={reconciling} className="gap-2">
                {reconciling ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                Complete Reconciliation
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      
      {/* Reconciliation History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Reconciliation History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : reconciliations.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Scale className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>No reconciliations yet</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-y">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Statement Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Bank Balance</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Book Balance</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Difference</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Reconciled</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {reconciliations.map((rec) => (
                  <tr key={rec.reconciliation_id} className="hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium">{formatDate(rec.statement_date)}</td>
                    <td className="py-3 px-4 text-right">{formatCurrency(rec.bank_balance)}</td>
                    <td className="py-3 px-4 text-right">{formatCurrency(rec.book_balance)}</td>
                    <td className={cn(
                      "py-3 px-4 text-right font-medium",
                      Math.abs(rec.difference) < 0.01 ? "text-green-600" : "text-red-600"
                    )}>
                      {formatCurrency(Math.abs(rec.difference))}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={
                        rec.status === 'balanced' || rec.status === 'completed'
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }>
                        {rec.status === 'balanced' || rec.status === 'completed' ? 'Balanced' : 'Discrepancy'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-500">{formatDate(rec.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Nightly Reconcile Section - Draw Batches
const NightlyReconcileSection = () => {
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [editingItems, setEditingItems] = useState({});

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    setLoading(true);
    try {
      const res = await api.get('/trust/draw-batches');
      setBatches(res.data?.batches || []);
    } catch (error) {
      console.error('Failed to load batches:', error);
      toast.error('Failed to load draw batches');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchDetails = async (batchId) => {
    try {
      const res = await api.get(`/trust/draw-batches/${batchId}`);
      setSelectedBatch(res.data);
    } catch (error) {
      console.error('Failed to load batch details:', error);
      toast.error('Failed to load batch details');
    }
  };

  const generateBatch = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/trust/draw-batches/generate?date=${selectedDate}`);
      toast.success(`Draw batch generated with ${res.data.items_count} items`);
      await loadBatches();
      if (res.data.batch_id) {
        await loadBatchDetails(res.data.batch_id);
      }
    } catch (error) {
      console.error('Failed to generate batch:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to generate batch');
    } finally {
      setGenerating(false);
    }
  };

  const saveItemEdits = async (batchId) => {
    if (Object.keys(editingItems).length === 0) {
      toast.info('No changes to save');
      return;
    }
    
    try {
      await api.put(`/trust/draw-batches/${batchId}`, { items: editingItems });
      toast.success('Changes saved');
      await loadBatchDetails(batchId);
      setEditingItems({});
    } catch (error) {
      console.error('Failed to save edits:', error);
      toast.error('Failed to save changes');
    }
  };

  const updateItemEdit = (itemId, field, value) => {
    setEditingItems(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [field]: value
      }
    }));
  };

  const markItemForRemoval = (itemId) => {
    setEditingItems(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        remove: true
      }
    }));
  };

  const approveBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to approve and post this batch? This will withdraw funds from trust accounts and cannot be undone.')) {
      return;
    }
    
    setApproving(true);
    try {
      const res = await api.post(`/trust/draw-batches/${batchId}/approve`);
      toast.success(`Batch approved and posted. Total: ${formatCurrency(res.data.total_amount)}`);
      await loadBatches();
      setSelectedBatch(null);
    } catch (error) {
      console.error('Failed to approve batch:', error);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.errors) {
        toast.error(
          <div>
            <p className="font-medium">{detail.message}</p>
            {detail.errors.map((e, i) => (
              <p key={i} className="text-sm">
                {e.case_title}: Need {formatCurrency(e.required_amount)}, Have {formatCurrency(e.available_balance)}
              </p>
            ))}
          </div>
        );
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to approve batch');
      }
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="nightly-reconcile-section">
      {/* Generate Batch Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Generate Nightly Draw Batch
          </CardTitle>
          <CardDescription>
            Generate a draw batch from approved time entries for billing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <Label>Statement Date</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-48"
                data-testid="batch-date-input"
              />
            </div>
            <Button 
              onClick={generateBatch}
              disabled={generating}
              className="gap-2"
              data-testid="generate-batch-btn"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Generate Batch
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Batch List */}
      <Card>
        <CardHeader>
          <CardTitle>Draw Batches</CardTitle>
        </CardHeader>
        <CardContent>
          {batches.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No draw batches found</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch ID</TableHead>
                  <TableHead>Statement Date</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Total Amount</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((batch) => (
                  <TableRow key={batch.batch_id}>
                    <TableCell className="font-mono text-sm">{batch.batch_id}</TableCell>
                    <TableCell>{formatDate(batch.statement_date)}</TableCell>
                    <TableCell>{batch.items_count}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(batch.total_amount)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={
                        batch.status === 'posted' ? 'bg-green-100 text-green-700' :
                        batch.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {batch.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(batch.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => loadBatchDetails(batch.batch_id)}
                        data-testid={`view-batch-${batch.batch_id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Batch Detail Dialog */}
      {selectedBatch && (
        <Dialog open={!!selectedBatch} onOpenChange={() => setSelectedBatch(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Draw Batch: {selectedBatch.batch_id}
                <Badge className={
                  selectedBatch.status === 'posted' ? 'bg-green-100 text-green-700' :
                  selectedBatch.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                  'bg-amber-100 text-amber-700'
                }>
                  {selectedBatch.status}
                </Badge>
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm text-slate-500">Statement Date</p>
                  <p className="font-medium">{formatDate(selectedBatch.statement_date)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm text-slate-500">Total Items</p>
                  <p className="font-medium">{selectedBatch.items?.length || 0}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-sm text-slate-500">Total Amount</p>
                  <p className="font-semibold text-purple-600">{formatCurrency(selectedBatch.total_amount)}</p>
                </div>
              </div>

              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Client / Case</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Trust Balance</TableHead>
                      {selectedBatch.status === 'draft' && (
                        <TableHead className="text-center">Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedBatch.items || []).map((item) => {
                      const itemEdit = editingItems[item.item_id] || {};
                      const isRemoved = itemEdit.remove;
                      const hasSufficientFunds = item.available_trust_balance >= item.amount;
                      
                      return (
                        <TableRow 
                          key={item.item_id}
                          className={cn(
                            isRemoved && "opacity-50 line-through",
                            !hasSufficientFunds && "bg-red-50"
                          )}
                        >
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.client_name}</p>
                              <p className="text-sm text-slate-500">{item.case_title}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {selectedBatch.status === 'draft' && !isRemoved ? (
                              <Input
                                defaultValue={item.description}
                                onChange={(e) => updateItemEdit(item.item_id, 'description', e.target.value)}
                                className="h-8 text-sm"
                              />
                            ) : (
                              item.description
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {selectedBatch.status === 'draft' && !isRemoved ? (
                              <Input
                                type="number"
                                step="0.1"
                                defaultValue={item.hours}
                                onChange={(e) => updateItemEdit(item.item_id, 'hours', parseFloat(e.target.value))}
                                className="h-8 w-20 text-sm text-right"
                              />
                            ) : (
                              item.hours?.toFixed(1)
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {selectedBatch.status === 'draft' && !isRemoved ? (
                              <Input
                                type="number"
                                step="1"
                                defaultValue={item.rate}
                                onChange={(e) => updateItemEdit(item.item_id, 'rate', parseFloat(e.target.value))}
                                className="h-8 w-24 text-sm text-right"
                              />
                            ) : (
                              formatCurrency(item.rate)
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(itemEdit.amount ?? item.amount)}
                          </TableCell>
                          <TableCell className={cn(
                            "text-right",
                            hasSufficientFunds ? "text-green-600" : "text-red-600"
                          )}>
                            {formatCurrency(item.available_trust_balance)}
                            {!hasSufficientFunds && (
                              <AlertTriangle className="h-4 w-4 inline ml-1" />
                            )}
                          </TableCell>
                          {selectedBatch.status === 'draft' && (
                            <TableCell className="text-center">
                              {!isRemoved ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => markItemForRemoval(item.item_id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingItems(prev => {
                                    const newItems = {...prev};
                                    delete newItems[item.item_id]?.remove;
                                    if (Object.keys(newItems[item.item_id] || {}).length === 0) {
                                      delete newItems[item.item_id];
                                    }
                                    return newItems;
                                  })}
                                  className="text-green-600"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-slate-500">
                  {selectedBatch.status === 'posted' && selectedBatch.approved_by && (
                    <span>Approved by {selectedBatch.approved_by} on {formatDate(selectedBatch.approved_at)}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setSelectedBatch(null)}>
                    Close
                  </Button>
                  
                  {selectedBatch.status === 'draft' && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => saveItemEdits(selectedBatch.batch_id)}
                        disabled={Object.keys(editingItems).length === 0}
                        className="gap-2"
                      >
                        <Edit2 className="h-4 w-4" />
                        Save Changes
                      </Button>
                      <Button
                        onClick={() => approveBatch(selectedBatch.batch_id)}
                        disabled={approving}
                        className="gap-2 bg-green-600 hover:bg-green-700"
                        data-testid="approve-batch-btn"
                      >
                        {approving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                        Approve & Post
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TrustAccounting;
